import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { BackToLibraryLink } from '@/components/BackToLibraryLink';
import { SiteFooter } from '@/components/SiteFooter';
import styles from './ErrorView.module.css';

interface RecoveryLink {
  to: string;
  label: string;
}

interface ErrorViewProps {
  eyebrow: string;
  title: string;
  message: string;
  errorId?: string;
  primaryAction?: {
    label: string;
    onClick?: () => void;
    to?: string;
  };
  secondaryAction?: {
    label: string;
    onClick?: () => void;
    to?: string;
  };
  recoveryLinks?: RecoveryLink[];
  supportLink?: RecoveryLink;
  /**
   * For a boundary outside the router and settings providers (AppErrorBoundary):
   * plain page links and no footer, since both need those providers.
   */
  standalone?: boolean;
}

/** A router link, or a plain page link when rendered outside the router. */
function NavLink({ to, className, standalone, children }: { to: string; className: string; standalone?: boolean; children: ReactNode }) {
  return standalone ? <a href={to} className={className}>{children}</a> : <Link to={to} className={className}>{children}</Link>;
}

function ActionButton({ label, onClick, className }: { label: string; onClick: () => void; className: string }) {
  return <button type="button" onClick={onClick} className={className}>{label}</button>;
}

export function ErrorView({
  eyebrow,
  title,
  message,
  errorId,
  primaryAction,
  secondaryAction,
  recoveryLinks = [],
  supportLink,
  standalone,
}: ErrorViewProps) {
  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        {standalone
          ? <a href="/" className={styles.back}>← Doopey's World</a>
          : <BackToLibraryLink href="/" className={styles.back} />}

        <section className={styles.panel}>
          <div className={styles.content}>
            <div className={styles.eyebrow}>{eyebrow}</div>
            <h1 className={styles.title}>{title}</h1>
            <p className={styles.text}>{message}</p>

            {errorId ? (
              <div className={styles.meta}>
                <span>If this keeps happening, send the error ID along with what you were doing.</span>
                <code className={styles.code}>{errorId}</code>
              </div>
            ) : null}

            {errorId ? (
              <div className={styles.support}>
                <span className={styles.supportLabel}>Want to help fix this?</span>
                <NavLink
                  to={`/bugreport?page=${encodeURIComponent(window.location.pathname)}&error=${encodeURIComponent(errorId)}`}
                  className={styles.supportLink}
                  standalone={standalone}
                >
                  Report a bug
                </NavLink>
              </div>
            ) : null}

            {(primaryAction || secondaryAction) ? (
              <div className={styles.actions}>
                {primaryAction?.to ? (
                  <NavLink to={primaryAction.to} className={styles.primary} standalone={standalone}>{primaryAction.label}</NavLink>
                ) : primaryAction?.onClick ? (
                  <ActionButton onClick={primaryAction.onClick} label={primaryAction.label} className={styles.primary} />
                ) : null}

                {secondaryAction?.to ? (
                  <NavLink to={secondaryAction.to} className={styles.secondary} standalone={standalone}>{secondaryAction.label}</NavLink>
                ) : secondaryAction?.onClick ? (
                  <ActionButton onClick={secondaryAction.onClick} label={secondaryAction.label} className={styles.secondary} />
                ) : null}
              </div>
            ) : null}

            {recoveryLinks.length > 0 ? (
              <div className={styles.links}>
                {recoveryLinks.map((link) => (
                  <NavLink key={link.to} to={link.to} className={styles.linkChip} standalone={standalone}>
                    <span aria-hidden="true">→</span>
                    <span>{link.label}</span>
                  </NavLink>
                ))}
              </div>
            ) : null}

            {supportLink ? (
              <div className={styles.support}>
                <span className={styles.supportLabel}>Think this page should have worked?</span>
                <NavLink to={supportLink.to} className={styles.supportLink} standalone={standalone}>{supportLink.label}</NavLink>
              </div>
            ) : null}
          </div>
        </section>

        {!standalone && <SiteFooter className={styles.footer} feedback />}
      </div>
    </div>
  );
}
